import { MAGPIE_CLIENT_LOGGER_NAME, WINDOW_BROKER_CONNECTOR_LOGGER_NAME, WINDOW_BROKER_LOCATOR_LOGGER_NAME, WINDOW_BROKER_LOGGER_NAME, WINDOW_BROKER_PROXY_LOGGER_NAME, CROSS_TAB_LINK_LOGGER_NAME, MetricsReporter } from '../../config';
import { MessageDispatchError, TopicSubscribeError, TopicSyncError } from '../../errors';
import { Logger, Level } from 'simple-logging-lib';
import { MagpieDebugHandler, MagpieErrorHandler } from '../../logging';
import { ObservableSet } from 'reactive-lib/collections/observable-set';
import { Subject } from 'reactive-lib/observable/subject';
import { getOrCreateBroker } from '../../window-broker';
import { AbstractConnectionStrategy } from '../abstract-connection-strategy';
import { getOrCreateFrameId, safeCallback } from '../../utils';
export class WindowBrokerStrategy extends AbstractConnectionStrategy {
  constructor(options) {
    super(options);
    this.logger = Logger.getLogger(MAGPIE_CLIENT_LOGGER_NAME);
    this.name = 'window-broker-strategy';
    this.handshakeResult = null;
    this.subscribedTopics = new ObservableSet();
    this.windowBroker = null;
    this.messageLink = new Subject();
    this.frameId = getOrCreateFrameId();
    this.setupLoggers = () => {
      const {
        debug,
        clientMetadata
      } = this.options;
      const loggingLevel = debug ? Level.DEBUG : Level.ERROR;
      const loggers = [WINDOW_BROKER_LOCATOR_LOGGER_NAME, WINDOW_BROKER_LOGGER_NAME, WINDOW_BROKER_PROXY_LOGGER_NAME, WINDOW_BROKER_CONNECTOR_LOGGER_NAME, CROSS_TAB_LINK_LOGGER_NAME];
      loggers.forEach(loggerName => {
        const logger = Logger.getLogger(loggerName);
        logger.setLevel(loggingLevel);
        logger.getHandlers().forEach(h => logger.removeHandler(h));
        logger.addHandler(new MagpieDebugHandler(clientMetadata));
        logger.addHandler(new MagpieErrorHandler(clientMetadata));
      });
    };
    this.topicSubscribe = topic => {
      if (this.handshakeResult === null) {
        throw new TopicSubscribeError(TopicSubscribeError.Reason.HANDSHAKE_FAILED, {
          messageArgs: [topic]
        });
      }
      if (!this.windowBroker) {
        throw new TopicSubscribeError(TopicSubscribeError.Reason.PROXY_UNAVAILABLE, {
          messageArgs: [topic]
        });
      }
      const {
        clientId
      } = this.handshakeResult;
      this.subscribedTopics.add(topic);
      return this.windowBroker.topicSubscribe({
        clientId,
        topic
      });
    };
    this.topicUnsubscribe = topic => {
      if (this.handshakeResult === null) {
        throw new TopicSubscribeError(TopicSubscribeError.Reason.HANDSHAKE_FAILED, {
          messageArgs: [topic]
        });
      }
      if (!this.windowBroker) {
        throw new TopicSubscribeError(TopicSubscribeError.Reason.PROXY_UNAVAILABLE, {
          messageArgs: [topic]
        });
      }
      this.subscribedTopics.delete(topic);
      const {
        clientId
      } = this.handshakeResult;
      return this.windowBroker.topicUnsubscribe({
        clientId,
        topic
      }).then(() => {
        this.logger.debug('Unsubscribed from topic', clientId, topic);
      });
    };
    this.onMessage = (messageType, topicName, subscriber) => {
      const subscription = this.messageLink.subscribe(message => {
        if (message.type === messageType && message.topic === topicName) {
          this.logger.debug('Got a message through a proxy:', message);
          subscriber(message);
        }
      });
      return () => subscription.unsubscribe();
    };
    this.sendMessage = mBuilder => {
      const type = mBuilder.getType();
      if (this.handshakeResult === null) {
        throw new MessageDispatchError(MessageDispatchError.Reason.HANDSHAKE_FAILED, {
          messageArgs: [type]
        });
      }
      if (!this.windowBroker) {
        throw new MessageDispatchError(MessageDispatchError.Reason.CONNECTION_UNAVAILABLE, {
          messageArgs: [type]
        });
      }
      const message = mBuilder.setDispatchedAt().setSender(this.handshakeResult.clientId).setSenderPortalId(this.options.clientMetadata.portalId || null).setSenderBrokerId(this.windowBroker.getBrokerId()).setSenderFrameId(this.frameId).build();
      this.logger.debug('Proxy sending message', message);
      this.windowBroker.sendMessage(message).then(() => {
        this.logger.debug('Proxy sent the message', message);
      }).catch(error => {
        this.logger.error(`Failed to send message.\n${error.message}`);
      });
    };
    this.onTopicSubscriptionsChange = onChange => {
      const safeOnChange = safeCallback(onChange);
      const listener = topics => {
        safeOnChange(Array.from(topics));
      };
      return this.subscribedTopics.subscribe(listener);
    };
    this.options = options;
    this.setupLoggers();
  }
  async open() {
    const startTime = Date.now();
    const windowBroker = await getOrCreateBroker();
    this.windowBroker = windowBroker;
    this.logger.debug('WindowBroker instance is available', windowBroker);
    const {
      clientMetadata
    } = this.options;
    this.handshakeResult = await windowBroker.handshake({
      clientMetadata
    }, this.messageLink);
    this.logger.debug('Handshake finished', clientMetadata, this.handshakeResult);
    const latency = Date.now() - startTime;
    MetricsReporter.reportHandshakeCompleted({
      appName: clientMetadata.appName
    }, latency);
    return {
      brokerId: windowBroker.getBrokerId(),
      clientId: this.handshakeResult.clientId,
      sendMessage: this.sendMessage,
      onMessage: this.onMessage,
      topicSubscribe: this.topicSubscribe,
      topicUnsubscribe: this.topicUnsubscribe
    };
  }
  async close() {
    if (this.handshakeResult === null) {
      return Promise.resolve();
    }
    if (this.windowBroker === null) {
      return Promise.resolve();
    }
    const {
      clientId
    } = this.handshakeResult;
    try {
      await this.windowBroker.close(clientId);
      this.logger.debug(`Connection closed - client ID #${clientId}`);
    } catch (error) {
      if (error instanceof Error) {
        this.logger.error(`An error occurred while closing the connection:\n${error.message}`);
      }
    }
  }
  getHandshakeResult() {
    return this.handshakeResult;
  }
  async getActiveTopicSubscriptions() {
    if (this.handshakeResult === null) {
      throw new TopicSyncError(TopicSyncError.Reason.HANDSHAKE_FAILED);
    }
    if (this.windowBroker === null) {
      throw new TopicSyncError(TopicSyncError.Reason.CONNECTION_UNAVAILABLE);
    }
    const {
      portalId
    } = this.options.clientMetadata;
    const {
      clientId
    } = this.handshakeResult;
    const subscriptions = await this.windowBroker.getTopicSubscriptions({
      clientId,
      portalId
    });
    return subscriptions.topicNames;
  }
}